/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/
import { IOrderSummaryLines, OrderSummary } from '../../../common/components/order-summary-lineitem';
import { Heading, INodeProps } from '@msdyn365-commerce-modules/utilities';
import get from 'lodash/get';
import * as React from 'react';
import { IFarmlandsCheckoutData } from '../farmlands-checkout.data';
import { IFarmlandsCheckoutProps } from '../farmlands-checkout.props.autogenerated';

export interface IOrderSummary {
    orderSummaryProps: INodeProps;
    heading?: React.ReactNode;
    account?: React.ReactNode;
    lines?: IOrderSummaryLines;
}
export const getOrderSummary = (
    input: IFarmlandsCheckoutProps<IFarmlandsCheckoutData>,
    displayName: Readonly<string | undefined>,
    accountNumber: Readonly<string | undefined>
): IOrderSummary => {
    const {
        config: { orderSummaryHeading },
        data: { checkout },
        resources: {
            subTotalLabel,
            shippingLabel,
            taxLabel,
            orderTotalLabel,
            loyaltyLabel,
            giftcardLabel,
            totalDiscountsLabel,
            freeText,
            toBeCalculatedText
        },
        context,
        typeName,
        id,
        telemetry
    } = input;

    const checkoutState = get(checkout, 'result');
    const cart = get(checkout, 'result.checkoutCart.cart');
    const showLineItems = cart.CartLines && cart.CartLines.length > 0;
    const channelConfiguration = context.request.channel!;
    const getOrderSummaryAccountDetails = (): React.ReactNode => {
        const { user } = input.context.request;

        return (
            user.isAuthenticated && (
                <div className='msc-order-summary__account'>
                    <div>Your Farmlands Account</div>
                    <div>
                        {accountNumber} - {displayName}
                    </div>
                </div>
            )
        );
    };

    return {
        orderSummaryProps: { className: 'ms-checkout-guest-profile__selected-item' },
        heading: orderSummaryHeading && <Heading className='msc-order-summary__heading' {...orderSummaryHeading} />,
        account: getOrderSummaryAccountDetails(),
        lines: showLineItems
            ? OrderSummary({
                  checkoutState,
                  cart,
                  subTotalLabel,
                  shippingLabel,
                  taxLabel,
                  orderTotalLabel,
                  loyaltyLabel,
                  giftcardLabel,
                  totalDiscountsLabel,
                  freeText,
                  toBeCalculatedText,
                  context,
                  typeName,
                  id,
                  channelConfiguration,
                  telemetry
              })
            : undefined
    };
};
